import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  inject,
  OnInit, DestroyRef, input,
} from '@angular/core';
import { BaseControlAccessorComponent } from '@iris/common/modules/fields/base-control/base-control-accessor.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslationsModalComponent } from '@iris/common/modules/fields/field-translate/translations-modal/translations-modal.component';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { MatTooltip } from '@angular/material/tooltip';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filterNil } from '@iris/common/utils/rxjs.utils';

@Component({
  selector: 'iris-field-translate',
  templateUrl: './field-translate.component.html',
  styleUrls: ['./field-translate.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTooltip,
    NgTemplateOutlet,
    TranslatePipe,
    StrakitButtonComponent,
  ],
})
export class IrisFieldTranslateComponent extends BaseControlAccessorComponent implements OnInit {
  dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  @Input() label = '';
  @Input() controlTemplate: TemplateRef<unknown>;

  @Input() translations: unknown;
  @Input() translatedFieldName: string;

  // Enable the translations reset when the main value change and the field is dirty. To programmatically have the same
  // result mark the control dirty before patching the new value.
  resetTranslationsOnValueChange = input(true);

  @Output() translationsChange = new EventEmitter<Record<string, string>>();

  ngOnInit(): void {
    this.outerControl?.valueChanges.pipe(
      filter(() => this.resetTranslationsOnValueChange()),
      filterNil(),
      distinctUntilChanged(),
      tap(() => {
        // 1. Only reset when the control is dirty, avoiding page-load programmatic patchValue resets
        if (!this.outerControl.dirty) {
          return;
        }

        // 2. Only reset and emit if there are actual translations to clear (avoids race conditions & redundant events)
        if (this.hasTranslationsToReset()) {
          this.resetTranslations();
        }
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  openTranslationsModal(): void {
    const dialogRef = this.dialog.open(
      TranslationsModalComponent, {
        data: {
          control: this.outerControl,
          controlTemplate: this.controlTemplate,
          translations: this.translations,
          translatedFieldName: this.translatedFieldName,
        },
        maxWidth: '500px',
      });

    dialogRef.componentInstance.result$.pipe(
      tap((translations) => this.translationsChange.emit(translations)),
    ).subscribe();
  }

  private hasTranslationsToReset(): boolean {
    if (!this.translations) {
      return false;
    }

    if (this.translatedFieldName) {
      // If translations have more than one translation and translatedFieldName is specified
      return Object.keys(this.translations).some(key =>
        key !== 'uuid' &&
        typeof this.translations[key] === 'object' &&
        this.translations[key] !== null &&
        this.translatedFieldName in this.translations[key],
      );
    } else { // If no translatedFieldName is specified check if has at least one translate key
      return Object.keys(this.translations).some(key =>
        key !== 'uuid' &&
        this.translations[key] !== undefined &&
        this.translations[key] !== null &&
        this.translations[key] !== '',
      );
    }
  }

  private resetTranslations(): void {
    const updatedTranslations = { ...this.translations as Record<string, unknown> };

    if (this.translatedFieldName) {
      // Remove only the specific field from each language object but exclude the uuid.
      Object.keys(updatedTranslations).forEach(key => {
        if (key !== 'uuid' && typeof updatedTranslations[key] === 'object' && updatedTranslations[key] !== null) {
          const langObj = { ...updatedTranslations[key] };
          delete langObj[this.translatedFieldName];

          if (Object.keys(langObj).length > 0) {
            updatedTranslations[key] = langObj;
          } else {
            delete updatedTranslations[key];
          }
        }
      });
    } else {
      // Remove all keys except uuid
      Object.keys(updatedTranslations).forEach(key => {
        if (key !== 'uuid') {
          delete updatedTranslations[key];
        }
      });
    }

    this.translations = updatedTranslations;
    this.translationsChange.emit(this.translations as Record<string, string>);
  }

}
